import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Badge, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { agentService } from '../../services/agentService';
import { isAdmin } from '../../services/auth';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import MajesticConfirmModal from '../common/MajesticConfirmModal';

const AgentDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    todayRegistrations: 0,
    totalBalance: 0
  });

  const [timeFilter, setTimeFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [announcements, setAnnouncements] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '', type: 'notice' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const adminUser = isAdmin();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await agentService.getDashboardStats();
        if (response.success) {
          setStats({
            totalUsers: response.stats?.totalUsers || 0,
            activeUsers: response.stats?.activeUsers || 0,
            todayRegistrations: response.stats?.todayRegistrations || 0,
            totalBalance: response.stats?.totalBalance || 0
          });
        }
      } catch (e) {
        console.error('Failed to fetch dashboard stats:', e);
        toast.error(t('error_fetch_stats', 'Failed to load dashboard statistics'));
      }
    };

    const fetchAnnouncements = async () => {
      try {
        const response = await agentService.getAnnouncements();
        if (response.success) {
          setAnnouncements(response.announcements);
        }
      } catch (e) { console.error(e); }
    };

    fetchData();
    fetchAnnouncements();
  }, [t]);

  const handlePostAnnouncement = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const resp = await agentService.createAnnouncement(newAnnouncement);
      if (resp.success) {
        toast.success(t('msg_save_success', 'Announcement posted'));
        setShowModal(false);
        setNewAnnouncement({ title: '', content: '', type: 'notice' });
        // Refresh list
        const updated = await agentService.getAnnouncements();
        if (updated.success) setAnnouncements(updated.announcements);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAnnouncement = (id) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAnnouncement = async () => {
    if (!deletingId) return;
    try {
      const resp = await agentService.deleteAnnouncement(deletingId);
      if (resp.success) {
        toast.success(t('msg_delete_success', 'Deleted'));
        setAnnouncements(announcements.filter(a => a.id !== deletingId));
        setShowDeleteConfirm(false);
        setDeletingId(null);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const InfoCard = ({ title, value, color, icon }) => (
    <Card className="majestic-card border-0 mb-0">
      <Card.Body className="d-flex align-items-center justify-content-between py-4">
        <div>
          <div className="text-secondary mb-1 fw-bold" style={{ fontSize: '11px', letterSpacing: '1px', opacity: 0.6 }}>{title}</div>
          <div className="h3 mb-0 fw-bold text-white" style={{ fontSize: '24px', letterSpacing: '0.5px' }}>{value}</div>
        </div>
        <div className="rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: '52px', height: '52px', backgroundColor: `${color}15`, color: color, border: `1px solid ${color}30` }}>
          <i className={`bi ${icon} fs-4`}></i>
        </div>
      </Card.Body>
    </Card>
  );

  const quickFunctions = [
    { name: t('shortcut_user_list'), icon: 'bi-people', color: 'var(--primary-blue)', path: '/user-management' },
    { name: t('shortcut_stock_orders'), icon: 'bi-graph-up', color: 'var(--warning-orange)', path: '/stock-orders' },
    { name: t('shortcut_withdrawal'), icon: 'bi-wallet2', color: 'var(--danger-red)', path: '/withdrawals' },
    { name: t('shortcut_audit_logs'), icon: 'bi-file-earmark-text', color: 'var(--success-green)', path: '/audit-logs' },
  ];

  return (
    <div className="majestic-page-wrapper p-3">
      {/* 1. Data Analysis Header */}
      <h5 className="mb-4 ps-1 border-start border-4 border-warning d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)', fontWeight: '800', letterSpacing: '0.5px' }}>
        <i className="bi bi-speedometer2 text-warning"></i>
        {t('workbench')} {t('overview')}
      </h5>

      <Row className="g-3 mb-4">
        <Col xs={6} md={3} className="d-flex flex-column">
          <InfoCard title={t('card_total_users')} value={stats.totalUsers} color="var(--primary-blue)" icon="bi-people-fill" />
        </Col>
        <Col xs={6} md={3} className="d-flex flex-column">
          <InfoCard title={t('card_active_users')} value={stats.activeUsers} color="#00bfa5" icon="bi-person-check-fill" />
        </Col>
        <Col xs={6} md={3} className="d-flex flex-column">
          <InfoCard title={t('card_total_balance')} value={`$${stats.totalBalance.toLocaleString()}`} color="#fcd535" icon="bi-wallet2" />
        </Col>
        <Col xs={6} md={3} className="d-flex flex-column">
          <InfoCard title={t('card_today_registration')} value={stats.todayRegistrations} color="#ff6b6b" icon="bi-person-plus-fill" />
        </Col>
      </Row>

      {/* Time Filter Section - Premium Design */}
      <Card className="majestic-card border-0 mb-4" style={{ background: 'linear-gradient(135deg, var(--bg-card) 0%, rgba(64, 158, 255, 0.03) 100%)' }}>
        <Card.Body className="p-4">
          <div className="d-flex align-items-center mb-4">
            <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '32px', height: '32px', background: 'rgba(64, 158, 255, 0.15)' }}>
              <i className="bi bi-calendar3 text-primary"></i>
            </div>
            <span className="fw-semibold" style={{ color: 'var(--text-primary)', fontSize: '15px' }}>{t('time')}</span>
            <div className="flex-grow-1 ms-3" style={{ height: '1px', background: 'linear-gradient(90deg, var(--border-color), transparent)' }}></div>
          </div>

          <Row className="g-3 align-items-center">
            <Col xs={12} md="auto">
              <div className="d-flex align-items-center rounded-3 overflow-hidden" style={{ border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-dark)', height: '42px' }}>
                <div className="px-3 text-secondary">
                  <i className="bi bi-clock"></i>
                </div>
                <Form.Control
                  id="startTime"
                  name="startTime"
                  type="text"
                  placeholder={t('start_time')}
                  className="bg-transparent border-0 shadow-none text-center"
                  style={{ width: '100px', fontSize: '13px', color: 'var(--text-primary)' }}
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                />
                <span className="text-secondary px-1">-</span>
                <Form.Control
                  id="endTime"
                  name="endTime"
                  type="text"
                  placeholder={t('end_time')}
                  className="bg-transparent border-0 shadow-none text-center"
                  style={{ width: '100px', fontSize: '13px', color: 'var(--text-primary)' }}
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                />
              </div>
            </Col>

            <Col xs={12} md="auto">
              <div className="d-flex flex-wrap gap-2">
                {[
                  { id: 'all', label: t('all') },
                  { id: 'today', label: t('today') },
                  { id: 'yesterday', label: t('yesterday') },
                  { id: 'seven', label: t('seven_days') },
                  { id: 'month', label: t('that_month') }
                ].map(btn => (
                  <Button
                    key={btn.id}
                    size="sm"
                    variant={timeFilter === btn.id ? 'primary' : 'outline-secondary'}
                    className="rounded-pill px-3 py-1 fw-bold transition-all"
                    style={{
                      fontSize: '11px',
                      background: timeFilter === btn.id
                        ? 'linear-gradient(135deg, #fcd535 0%, #f0b90b 100%)'
                        : 'rgba(255, 255, 255, 0.05)',
                      border: `1px solid ${timeFilter === btn.id ? '#fcd535' : 'rgba(255, 255, 255, 0.1)'}`,
                      color: timeFilter === btn.id ? '#000' : 'var(--text-secondary)',
                      boxShadow: timeFilter === btn.id ? '0 4px 10px rgba(252, 213, 53, 0.25)' : 'none',
                      minWidth: '80px',
                      textTransform: 'uppercase'
                    }}
                    onClick={() => setTimeFilter(btn.id)}
                  >
                    {btn.label}
                  </Button>
                ))}
              </div>
            </Col>

            <Col xs={12} md="auto" className="ms-md-auto">
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  className="rounded-pill px-4 py-2 fw-bold border-0"
                  style={{ fontSize: '13px', background: 'linear-gradient(135deg, #fcd535 0%, #f0b90b 100%)', color: '#000', boxShadow: '0 4px 12px rgba(252, 213, 53, 0.3)' }}
                >
                  <i className="bi bi-search me-2"></i>{t('query')}
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="rounded-pill px-4 py-2"
                  style={{ fontSize: '13px', borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                  onClick={() => {
                    setTimeFilter('all');
                    setDateRange({ start: '', end: '' });
                  }}
                >
                  {t('reset')}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 2. System Announcements Section - Premium Design */}
      <Card className="majestic-card border-0 mb-4 overflow-hidden" style={{ background: 'linear-gradient(180deg, var(--bg-card) 0%, rgba(64, 158, 255, 0.02) 100%)' }}>
        <Card.Header
          className="border-0 d-flex justify-content-between align-items-center py-4 px-4"
          style={{ background: 'linear-gradient(90deg, rgba(64, 158, 255, 0.08) 0%, transparent 50%)' }}
        >
          <div className="d-flex align-items-center">
            <div
              className="rounded-3 d-flex align-items-center justify-content-center me-3"
              style={{
                width: '42px',
                height: '42px',
                background: 'linear-gradient(135deg, var(--primary-blue) 0%, #66b1ff 100%)',
                boxShadow: '0 4px 15px rgba(64, 158, 255, 0.35)'
              }}
            >
              <i className="bi bi-megaphone-fill text-white fs-5"></i>
            </div>
            <div>
              <span className="fw-bold d-block" style={{ color: 'var(--text-primary)', fontSize: '16px' }}>{t('announcement_title')}</span>
              <span className="text-secondary" style={{ fontSize: '12px' }}>{t('latest_updates', 'Latest updates and notices')}</span>
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {adminUser && (
              <Button
                size="sm"
                className="rounded-pill px-3 py-2 fw-bold border-0 d-flex align-items-center"
                style={{
                  fontSize: '12px',
                  background: 'linear-gradient(135deg, #fcd535 0%, #f0b90b 100%)',
                  color: '#000',
                  boxShadow: '0 4px 12px rgba(252, 213, 53, 0.3)'
                }}
                onClick={() => setShowModal(true)}
              >
                <i className="bi bi-plus-lg me-1"></i>{t('post', 'Post')}
              </Button>
            )}
            <Button
              variant="link"
              className="text-secondary p-0 text-decoration-none d-flex align-items-center"
              style={{ fontSize: '13px' }}
            >
              {t('view_more')} <i className="bi bi-chevron-right ms-1"></i>
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {announcements.length === 0 ? (
            <div className="py-5 text-center">
              <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px', background: 'rgba(144, 147, 153, 0.1)' }}>
                <i className="bi bi-inbox text-secondary fs-3"></i>
              </div>
              <div className="text-secondary">{t('no_data')}</div>
              <div className="text-secondary small mt-1">{t('no_announcements_yet', 'No announcements to display')}</div>
            </div>
          ) : (
            announcements.map((item, index, arr) => (
              <div
                key={item.id}
                className={`d-flex align-items-center justify-content-between p-3 announcement-item ${index !== arr.length - 1 ? 'border-bottom' : ''}`}
                style={{ borderColor: 'var(--border-light)', cursor: 'pointer' }}
              >
                <div className="d-flex align-items-center overflow-hidden">
                  <Badge
                    className="me-3 border-0 py-1"
                    style={{
                      backgroundColor: item.type === 'maintenance' ? 'rgba(255, 152, 0, 0.15)' :
                        item.type === 'update' ? 'rgba(76, 175, 80, 0.15)' :
                          'rgba(33, 150, 243, 0.15)',
                      color: item.type === 'maintenance' ? '#ff9800' :
                        item.type === 'update' ? '#4caf50' :
                          '#2196f3',
                      fontSize: '11px',
                      fontWeight: '500',
                      width: '75px',
                      textAlign: 'center'
                    }}
                  >
                    {t(item.type)}
                  </Badge>
                  <span className="announcement-title-text text-truncate" style={{ fontSize: '14px', color: 'var(--text-regular)' }}>{item.title}</span>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span className="text-secondary smaller">{new Date(item.createdAt).toLocaleString('en-GB', { hour12: false })}</span>
                  {adminUser && (
                    <i
                      className="bi bi-trash text-danger cursor-pointer hover-scale"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAnnouncement(item.id);
                      }}
                    ></i>
                  )}
                </div>
              </div>
            ))
          )}
        </Card.Body>
      </Card>

      {/* 3. Quick Functions / Shortcuts */}
      <h6 className="mb-3 text-secondary ms-1" style={{ fontSize: '13px', fontWeight: '600' }}>{t('quick_functions')}</h6>
      <Row className="g-3 mb-4">
        {quickFunctions.map((item, idx) => (
          <Col xs={6} md={3} key={idx}>
            <Card
              className="majestic-card h-100 border-0 text-center py-4 shortcut-card quick-function-btn"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(item.path)}
            >
              <div className="mb-3">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle p-3" style={{ backgroundColor: `${item.color}15` }}>
                  <i className={`bi ${item.icon} fs-1`} style={{ color: item.color }}></i>
                </div>
              </div>
              <h6 className="mb-0 fw-bold" style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{item.name}</h6>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Post Announcement Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        contentClassName="majestic-card border-0"
      >
        <Modal.Header closeButton className="border-bottom border-secondary border-opacity-10 py-3">
          <Modal.Title className="fw-bold text-white fs-5">{t('create_new_announcement', 'Create Announcement')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePostAnnouncement}>
          <Modal.Body className="p-4">
            <Form.Group className="mb-3">
              <Form.Label className="majestic-label-text ps-1">{t('type')}</Form.Label>
              <div className="majestic-input-wrapper">
                <i className="bi bi-megaphone-fill majestic-input-icon"></i>
                <Form.Select
                  id="announcementType"
                  name="announcementType"
                  className="majestic-input-field pe-5"
                  style={{
                    cursor: 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    background: 'transparent',
                    border: 'none',
                    paddingRight: '30px'
                  }}
                  value={newAnnouncement.type}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, type: e.target.value })}
                >
                  <option value="notice" className="bg-dark text-white">{t('notice')}</option>
                  <option value="update" className="bg-dark text-white">{t('update')}</option>
                  <option value="maintenance" className="bg-dark text-white">{t('maintenance')}</option>
                </Form.Select>
                <i className="bi bi-chevron-down text-secondary position-absolute end-0 me-3 pointer-events-none" style={{ fontSize: '10px' }}></i>
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="majestic-label-text ps-1">{t('title')}</Form.Label>
              <div className="majestic-input-wrapper">
                <i className="bi bi-fonts majestic-input-icon"></i>
                <Form.Control
                  id="announcementTitle"
                  name="announcementTitle"
                  className="majestic-input-field"
                  placeholder={t('enter_title', 'Enter announcement title')}
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                  required
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label className="majestic-label-text ps-1">{t('content')}</Form.Label>
              <div className="majestic-input-wrapper align-items-start py-2">
                <i className="bi bi-text-paragraph majestic-input-icon mt-2"></i>
                <Form.Control
                  id="announcementContent"
                  name="announcementContent"
                  as="textarea"
                  rows={4}
                  className="majestic-input-field"
                  style={{ resize: 'none' }}
                  placeholder={t('enter_details', 'Enter announcement details...')}
                  value={newAnnouncement.content}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                  required
                />
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-top border-secondary border-opacity-10">
            <Button variant="link" className="text-secondary text-decoration-none" onClick={() => setShowModal(false)}>
              {t('cancel')}
            </Button>
            <Button variant="warning" type="submit" className="px-4 fw-bold" disabled={submitting}>
              {submitting ? t('processing') : t('post')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <MajesticConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteAnnouncement}
        title={t('confirm_delete', 'Confirm Delete')}
        message={t('confirm_announcement_delete', 'Are you sure you want to delete this announcement? This action cannot be undone.')}
        confirmText={t('delete', 'Delete')}
        variant="danger"
        icon="bi-trash3-fill"
      />

      <style>{`
        .shortcut-card:hover { transform: translateY(-5px); transition: all 0.3s ease; background-color: var(--border-light) !important; box-shadow: 0 5px 15px rgba(0,0,0,0.1); }
        .announcement-item:hover { background-color: rgba(var(--primary-blue), 0.02); }
        .announcement-item:hover .announcement-title-text { color: var(--primary-blue) !important; }
        .hover-primary:hover { color: var(--primary-blue) !important; }
        .time-filter-card .form-control::placeholder { color: #888; }
        .hover-scale:hover { transform: scale(1.2); transition: transform 0.2s; }
        .pointer-events-none { pointer-events: none; }
        .majestic-input-field:focus { outline: none; }
        /* Fix for select background in some browsers */
        select.majestic-input-field option { background-color: #151a23 !important; color: white !important; }

        @media (max-width: 768px) {
            .quick-function-btn { padding: 12px 8px !important; }
            .quick-function-btn i { font-size: 24px !important; margin-bottom: 8px !important; }
            .quick-function-btn span { font-size: 11px !important; }
            .time-filter-card .d-flex.flex-wrap { justify-content: space-between; }
            .time-filter-card .btn-group { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default AgentDashboard;